import { useCallback, useMemo, useRef } from 'react';
import { ApolloError } from '@apollo/client';

import {
  FakturowniaUnmatchedCountDocument,
  InvoiceIssuePreflightQuery,
  IssueVatInvoiceForContributionMutation,
  StudentDetailDocument,
  useInvoiceIssuePreflightQuery,
  useIssueVatInvoiceForContributionMutation,
} from 'core/graphql';
import { documentMayExist } from 'core/utils/invoiceIssueReasons';

import { evictInvoiceIssueLists } from './evictFinanceLists';
import { isPermissionDeniedError } from './errors';

/** Server verdict for one (agreement, взнос) pair — what the POST will carry. */
export type IInvoiceIssuePreflight = NonNullable<
  InvoiceIssuePreflightQuery['fakturowniaContributionBillability']
>;

/** The invoice as returned by a successful issue. */
export type IIssuedInvoice = NonNullable<
  NonNullable<
    IssueVatInvoiceForContributionMutation['issueInvoiceForContribution']
  >['invoice']
>;

/** Normalized outcome of one issue attempt. */
export interface IIssueFakturaResult {
  ok: boolean;
  /** Machine reason code on failure (`errorCode`), `null` on success. */
  errorCode: string | null;
  /** Backend messages, already filtered of nullish entries. */
  errors: string[];
  /** The mirrored invoice, present on success. */
  invoice: IIssuedInvoice | null;
  /**
   * A real fiscal document may exist in Fakturownia despite `ok === false`.
   * Covers BOTH `documentCreated === true` and `FAKTUROWNIA_AMBIGUOUS`.
   */
  documentAtRisk: boolean;
}

interface IUseIssueFakturaArgs {
  /** Relay global id of the agreement (`agreement.id` from StudentDetail). */
  agreementId: string;
  /** Relay global id of the взнос. Omit → the server picks the next billable one. */
  contributionId?: string;
  /**
   * Relay global id of the student, used for the `StudentDetail` refetch.
   * MUST be the global id (`student.id` from the query), not the raw `:id`
   * URL param — `useStudentDetail` watches the query under the encoded id, so
   * a refetch keyed by the bare pk would target a query nobody is watching
   * and the schedule would not repaint.
   */
  studentId?: string;
  /** Skip the preflight until the popup is actually open. */
  skipPreflight?: boolean;
}

/**
 * The hook's surface. Exported because the flow's UI lifts the hook to the
 * component that owns the row actions (so an in-flight issue can freeze them)
 * and passes it into the popup, which stays presentational.
 */
export interface IIssueFakturaApi {
  preflight: IInvoiceIssuePreflight | null;
  preflightLoading: boolean;
  preflightError?: ApolloError;
  permissionDenied: boolean;
  refetchPreflight: () => void;
  issue: () => Promise<IIssueFakturaResult>;
  issuing: boolean;
}

const NO_ERRORS: string[] = [];

/**
 * «Выставить фактуру» — issue a REAL Fakturownia VAT document for one взнос.
 *
 * Two halves that must stay together:
 *   - the PREFLIGHT (`fakturowniaContributionBillability`) runs the exact same
 *     `check_billability()` the mutation runs, so the confirm screen shows
 *     what the server is about to POST — buyer identity included;
 *   - the MUTATION creates a document that receives a fiscal number and
 *     CANNOT be deleted from the ERP.
 *
 * Because of that irreversibility this hook carries a synchronous `useRef`
 * in-flight guard on top of Apollo's `loading`. React state updates are
 * async: a fast double-click dispatches both handlers in the same tick,
 * before `loading` has flipped, and the vendor has no idempotency key — the
 * second call would create a SECOND real document. The ref flips before the
 * first `await`, so the second call returns without issuing.
 *
 * `fetchPolicy: 'network-only'` on the preflight is deliberate: an operator
 * must never confirm a fiscal document against a cached verdict that another
 * tab (or the hourly matcher) has since invalidated.
 */
export const useIssueFaktura = ({
  agreementId,
  contributionId,
  studentId,
  skipPreflight = false,
}: IUseIssueFakturaArgs): IIssueFakturaApi => {
  const {
    data,
    loading: preflightLoading,
    error: preflightError,
    refetch,
  } = useInvoiceIssuePreflightQuery({
    variables: { agreementId, contributionId },
    skip: skipPreflight || !agreementId,
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
  });

  const refetchQueries = useMemo(() => {
    const queries: Array<
      | { query: typeof FakturowniaUnmatchedCountDocument }
      | { query: typeof StudentDetailDocument; variables: { id: string } }
    > = [{ query: FakturowniaUnmatchedCountDocument }];
    if (studentId) {
      queries.push({
        query: StudentDetailDocument,
        variables: { id: studentId },
      });
    }
    return queries;
    // NOTE: deliberately NO no-variables FakturowniaInvoicesDocument entry —
    // that would run an UNBOUNDED company-wide invoice query on every issue
    // (see the anti-pattern note in useInvoiceMatching.ts).
  }, [studentId]);

  const [issueMutation, issueState] = useIssueVatInvoiceForContributionMutation(
    {
      refetchQueries,
      update: evictInvoiceIssueLists,
      // The popup's success phase reads the refreshed schedule; without this
      // the operator can act on pre-issue numbers.
      awaitRefetchQueries: true,
    },
  );

  // Synchronous double-submit guard — see the hook docstring.
  const inFlight = useRef(false);

  const issue = useCallback(async (): Promise<IIssueFakturaResult> => {
    if (inFlight.current) {
      return {
        ok: false,
        errorCode: 'ISSUE_IN_FLIGHT',
        errors: NO_ERRORS,
        invoice: null,
        documentAtRisk: false,
      };
    }
    inFlight.current = true;
    try {
      const result = await issueMutation({
        variables: { agreementId, contributionId },
      });
      const payload = result.data?.issueInvoiceForContribution;
      const errors = (payload?.errors || []).filter((e): e is string =>
        Boolean(e),
      );
      return {
        ok: Boolean(payload?.success),
        errorCode: payload?.success ? null : payload?.errorCode || null,
        errors,
        invoice: payload?.invoice || null,
        documentAtRisk: payload?.success
          ? false
          : documentMayExist(payload?.errorCode, payload?.documentCreated),
      };
    } catch (error) {
      // A transport failure on OUR side says nothing about whether the
      // backend's POST to Fakturownia landed — the server may well have
      // created a document we never heard about. Flag it as at-risk rather
      // than reporting a clean failure.
      return {
        ok: false,
        errorCode: 'FAKTUROWNIA_AMBIGUOUS',
        errors: [(error as ApolloError).message],
        invoice: null,
        documentAtRisk: true,
      };
    } finally {
      inFlight.current = false;
    }
  }, [issueMutation, agreementId, contributionId]);

  return {
    preflight: data?.fakturowniaContributionBillability || null,
    preflightLoading,
    preflightError,
    permissionDenied: isPermissionDeniedError(preflightError),
    refetchPreflight: () => {
      refetch();
    },
    issue,
    issuing: issueState.loading,
  };
};
